import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Fade, Slide, Zoom } from "react-awesome-reveal";
import {
  FaCalendarAlt,
  FaCertificate,
  FaCheckCircle,
  FaChevronRight,
  FaFlagCheckered,
  FaLayerGroup,
  FaMedal,
  FaTrophy,
} from "react-icons/fa";
import { GiKimono } from "react-icons/gi";

import { getAdherentsByUser } from "./../../services/adherentServices";
import {
  getBeltHistoryByAdherent,
  getBeltsList,
  getHomeDemoBeltHistory,
} from "./../../services/beltServices";
import { checkGrade } from "./../../services/gradCnServices";

const MAX_GRADES_HOME = 4;

const normalizeAdherentsResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.adherents)) return response.adherents;
  if (Array.isArray(response?.Adherents)) return response.Adherents;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.adherents)) return response.data.adherents;
  return [];
};

const normalizeBeltHistoryResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.BeltHistory)) return response.BeltHistory;
  if (Array.isArray(response?.beltHistory)) return response.beltHistory;
  if (Array.isArray(response?.history)) return response.history;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.BeltHistory)) return response.data.BeltHistory;
  return [];
};

const normalizeBeltsResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Belts)) return response.Belts;
  if (Array.isArray(response?.belts)) return response.belts;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const getGradeDate = (grade) =>
  grade?.date_passage || grade?.date || grade?.createdAt;

const getBelt = (grade) => grade?.BeltColor || grade?.beltColor || grade?.belt;

const getAdherentLabel = (grade) => {
  const adherent = grade?._homeAdherent || grade?.Adherent;

  if (!adherent?.prenom && !adherent?.nom) return "";

  return [adherent.prenom, adherent.nom].filter(Boolean).join(" ");
};

const getAdherentOptionLabel = (adherent) => {
  if (!adherent?.prenom && !adherent?.nom) {
    return `Adhérent #${adherent?.id || ""}`;
  }

  return [adherent.prenom, adherent.nom].filter(Boolean).join(" ");
};

const cleanText = (value) => {
  if (!value) return "";
  return String(value).replace(/<[^>]*>/g, "").trim();
};

const isValidDate = (dateValue) => {
  const date = new Date(dateValue);
  return Number.isFinite(date.getTime());
};

const getTimestamp = (dateValue) => {
  const date = new Date(dateValue);
  return Number.isFinite(date.getTime()) ? date.getTime() : 0;
};

const sortGradesByDateDesc = (items) =>
  [...items].sort(
    (a, b) => getTimestamp(getGradeDate(b)) - getTimestamp(getGradeDate(a)),
  );

const dedupeGrades = (items) => {
  const map = new Map();

  items.forEach((item) => {
    const key =
      item?.id ||
      `${item?.adherent_id}-${item?.belt_id}-${getGradeDate(item)}`;

    if (key && !map.has(key)) {
      map.set(key, item);
    }
  });

  return Array.from(map.values());
};

const formatDate = (dateValue) => {
  if (!isValidDate(dateValue)) return "Date non précisée";

  return new Date(dateValue).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const formatShortDate = (dateValue) => {
  if (!isValidDate(dateValue)) return "Date non précisée";

  return new Date(dateValue).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getBeltImage = (belt) => {
  if (!belt?.belt_url) return "/img/cover/cover_belt.webp";
  return `/img/belt/${belt.belt_url}`;
};

const findNextBelt = (latestGrade, belts) => {
  const currentBelt = getBelt(latestGrade);

  if (!latestGrade || !belts.length) return null;

  const currentIndex = belts.findIndex(
    (belt) =>
      Number(belt.id) === Number(latestGrade.belt_id) ||
      Number(belt.ordre) === Number(currentBelt?.ordre),
  );

  if (currentIndex < 0) return null;

  return belts[currentIndex + 1] || null;
};

function PassageGradeSkeleton() {
  return (
    <div className="grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
      <div className="min-h-[280px] animate-pulse rounded-[2rem] bg-slate-200 dark:bg-white/10" />

      <div className="space-y-3">
        {[1, 2, 3].map((item) => (
          <div
            key={item}
            className="h-24 animate-pulse rounded-[1.5rem] bg-slate-200 dark:bg-white/10"
          />
        ))}
      </div>
    </div>
  );
}

function AdherentSelect({
  isConnected,
  adherents,
  selectedAdherentId,
  onChange,
}) {
  if (!isConnected || adherents.length <= 1) return null;

  return (
    <Fade direction="up" triggerOnce delay={180}>
      <div className="rounded-[1.4rem] border border-slate-200 bg-white p-3 shadow-sm dark:border-white/10 dark:bg-white/[0.06]">
        <label
          htmlFor="passage-grade-adherent"
          className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400"
        >
          Judoka affiché
        </label>

        <select
          id="passage-grade-adherent"
          value={selectedAdherentId}
          onChange={(event) => onChange(event.target.value)}
          className="min-h-11 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-bold text-slate-800 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-500/10 dark:border-white/10 dark:bg-slate-950 dark:text-white"
        >
          {adherents.map((adherent) => (
            <option key={adherent.id} value={String(adherent.id)}>
              {getAdherentOptionLabel(adherent)}
            </option>
          ))}
        </select>
      </div>
    </Fade>
  );
}

function PassageGradeEmptyState({ isConnected, onAction, hasAdherents }) {
  return (
    <Fade direction="up" triggerOnce>
      <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-3 shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20">
        <div className="grid gap-4 rounded-[1.6rem] bg-slate-50 p-4 dark:bg-white/[0.04] md:grid-cols-[0.85fr_1.15fr] md:items-center">
          <div className="relative min-h-52 overflow-hidden rounded-[1.4rem] bg-slate-900 md:min-h-60">
            <img
              src="/img/cover/cover_belt.webp"
              alt="Passages de grades Judo Coach Pro"
              className="absolute inset-0 h-full w-full object-cover opacity-80 transition duration-700 hover:scale-105"
            />

            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/35 to-transparent" />

            <div className="absolute bottom-4 left-4 right-4 text-white">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] backdrop-blur-xl">
                <FaMedal />
                Grades
              </div>

              <p className="mt-3 font-heading text-xl font-bold leading-tight sm:text-2xl">
                {isConnected
                  ? hasAdherents
                    ? "La progression de ce judoka apparaîtra ici"
                    : "Aucun judoka associé au compte"
                  : "Aucun passage de grade de démonstration"}
              </p>
            </div>
          </div>

          <div className="px-1 py-2 md:px-3">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-600 dark:text-sky-300">
              {isConnected ? "Aucun passage" : "Compte démo"}
            </p>

            <h3 className="mt-3 font-heading text-2xl font-bold leading-tight text-slate-950 dark:text-white">
              {isConnected
                ? hasAdherents
                  ? "Les ceintures de ce judoka seront affichées dès qu’elles seront ajoutées."
                  : "Ajoutez d’abord un adhérent pour suivre sa progression."
                : "Ajoutez quelques passages de grades au compte démo pour valoriser cette section."}
            </h3>

            <p className="mt-3 max-w-xl text-sm font-medium leading-6 text-slate-600 dark:text-white/55">
              Judo Coach Pro garde une trace claire des passages de grades, de
              la dernière ceinture obtenue et de la prochaine étape.
            </p>

            <button
              type="button"
              onClick={onAction}
              className="mt-5 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-sky-600 to-blue-800 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-sky-600/20 transition hover:-translate-y-0.5 active:translate-y-0 sm:w-auto font-ui"
            >
              {isConnected ? "Voir mes grades" : "Créer mon compte"}
              <FaChevronRight className="text-xs" />
            </button>
          </div>
        </div>
      </div>
    </Fade>
  );
}

function LatestBeltCard({ grade, nextBelt }) {
  const belt = getBelt(grade);
  const adherentLabel = getAdherentLabel(grade);

  return (
    <Zoom triggerOnce>
      <article className="relative h-full overflow-hidden rounded-[2rem] border border-slate-200 bg-slate-950 p-4 text-white shadow-2xl shadow-slate-300/70 dark:border-white/10 dark:shadow-black/30">
        <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-sky-500/20 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 h-56 w-56 rounded-full bg-orange-500/20 blur-3xl" />

        <div className="relative">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-2 text-xs font-bold uppercase tracking-[0.18em] text-sky-100 backdrop-blur-xl">
            <FaTrophy />
            Dernière ceinture
          </div>

          <div className="mt-4 grid gap-4 md:grid-cols-[0.9fr_1.1fr] md:items-center">
            <div className="flex min-h-44 items-center justify-center rounded-[1.5rem] border border-white/10 bg-white/10 p-4 backdrop-blur-xl">
              <img
                src={getBeltImage(belt)}
                alt={belt?.nom || "Ceinture"}
                className="max-h-44 w-full object-contain drop-shadow-2xl"
              />
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-white/45">
                Grade actuel
              </p>

              <h3 className="mt-2 font-heading text-3xl font-bold leading-tight text-white sm:text-4xl">
                {belt?.nom || "Ceinture non renseignée"}
              </h3>

              {adherentLabel && (
                <p className="mt-2 text-sm font-bold text-sky-200">
                  {adherentLabel}
                </p>
              )}

              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl border border-white/10 bg-white/10 px-4 py-3 backdrop-blur-xl">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">
                    Date
                  </p>

                  <p className="mt-1 text-sm font-bold text-white">
                    {formatDate(getGradeDate(grade))}
                  </p>
                </div>

                {nextBelt && (
                  <div className="rounded-2xl border border-orange-300/20 bg-orange-500/15 px-4 py-3 backdrop-blur-xl">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-orange-100/60">
                      Prochaine
                    </p>

                    <p className="mt-1 text-sm font-bold text-orange-100">
                      {nextBelt.nom}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </article>
    </Zoom>
  );
}

function GradeTimelineItem({ grade, index }) {
  const belt = getBelt(grade);
  const adherentLabel = getAdherentLabel(grade);

  return (
    <Fade direction="up" triggerOnce delay={index * 60}>
      <article className="group rounded-[1.5rem] border border-slate-200 bg-white p-3 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg dark:border-white/10 dark:bg-white/[0.05]">
        <div className="flex gap-3">
          <div className="flex h-16 w-20 shrink-0 items-center justify-center rounded-2xl bg-slate-50 p-2 dark:bg-white/[0.06]">
            <img
              src={getBeltImage(belt)}
              alt={belt?.nom || "Ceinture"}
              className="max-h-full w-full object-contain"
              loading="lazy"
            />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">
                  Passage #{index + 2}
                </p>

                <h4 className="mt-1 line-clamp-1 font-heading text-base font-bold text-slate-950 dark:text-white">
                  {belt?.nom || "Ceinture non renseignée"}
                </h4>
              </div>

              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-2xl bg-sky-100 text-sky-700 dark:bg-sky-500/10 dark:text-sky-200">
                <FaCertificate className="text-sm" />
              </div>
            </div>

            <p className="mt-2 flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-white/50">
              <FaCalendarAlt className="shrink-0 text-orange-500" />
              {formatShortDate(getGradeDate(grade))}
            </p>

            {adherentLabel && (
              <p className="mt-1 line-clamp-1 text-xs font-bold text-sky-700 dark:text-sky-200">
                {adherentLabel}
              </p>
            )}
          </div>
        </div>
      </article>
    </Fade>
  );
}

function GradeCnCard({ gradeCN, nextBelt }) {
  if (!gradeCN) return null;

  const uvItems = [
    {
      label: "UV1",
      value: Boolean(gradeCN.uv1),
    },
    {
      label: "UV2",
      value: Boolean(gradeCN.uv2),
    },
    {
      label: "UV4",
      value: Boolean(gradeCN.uv4),
    },
  ];

  return (
    <Fade direction="up" triggerOnce>
      <article className="mt-5 overflow-hidden rounded-[1.8rem] border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-white/[0.05]">
        <div className="grid gap-4 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-orange-100 px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-orange-700 dark:bg-orange-500/10 dark:text-orange-200">
              <FaFlagCheckered />
              Préparation CN
            </div>

            <h3 className="mt-3 font-heading text-xl font-bold text-slate-950 dark:text-white">
              Suivi du grade supérieur
            </h3>

            <p className="mt-1 text-sm font-medium leading-6 text-slate-500 dark:text-white/45">
              Dominante :{" "}
              <span className="font-bold text-slate-800 dark:text-white/80">
                {gradeCN.dominante || "Non renseignée"}
              </span>
            </p>

            <div className="mt-4 grid gap-2 sm:grid-cols-4">
              {uvItems.map((item) => (
                <div
                  key={item.label}
                  className="rounded-2xl bg-slate-50 p-3 dark:bg-white/[0.05]"
                >
                  <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">
                    {item.label}
                  </p>

                  <p
                    className={`mt-2 flex items-center gap-2 text-xs font-bold ${
                      item.value
                        ? "text-emerald-600 dark:text-emerald-300"
                        : "text-slate-500 dark:text-white/45"
                    }`}
                  >
                    <FaCheckCircle />
                    {item.value ? "Validé" : "À faire"}
                  </p>
                </div>
              ))}

              <div className="rounded-2xl bg-slate-50 p-3 dark:bg-white/[0.05]">
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">
                  UV3
                </p>

                <p className="mt-2 text-xs font-bold text-slate-800 dark:text-white/80">
                  {gradeCN.uv3_points || 0} pts
                </p>
              </div>
            </div>

            {cleanText(gradeCN.observations) && (
              <p className="mt-4 line-clamp-2 rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm font-medium leading-6 text-slate-600 dark:border-white/10 dark:bg-white/[0.05] dark:text-white/55">
                {cleanText(gradeCN.observations)}
              </p>
            )}
          </div>

          <div className="rounded-[1.5rem] bg-slate-50 p-4 text-center dark:bg-white/[0.05]">
            {nextBelt ? (
              <>
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
                  Objectif
                </p>

                <img
                  src={getBeltImage(nextBelt)}
                  alt={nextBelt.nom}
                  className="mx-auto mt-3 h-24 w-full object-contain"
                />

                <p className="mt-2 font-heading text-lg font-bold text-slate-950 dark:text-white">
                  {nextBelt.nom}
                </p>
              </>
            ) : (
              <>
                <GiKimono className="mx-auto text-4xl text-sky-600 dark:text-sky-300" />

                <p className="mt-2 font-heading text-lg font-bold text-slate-950 dark:text-white">
                  Objectif à définir
                </p>
              </>
            )}
          </div>
        </div>
      </article>
    </Fade>
  );
}

function PassageGradeHome({ isConnected }) {
  const navigate = useNavigate();

  const [managedAdherents, setManagedAdherents] = useState([]);
  const [selectedAdherentId, setSelectedAdherentId] = useState("");
  const [adherentsLoaded, setAdherentsLoaded] = useState(!isConnected);

  const [grades, setGrades] = useState([]);
  const [belts, setBelts] = useState([]);
  const [gradeCN, setGradeCN] = useState(null);
  const [loading, setLoading] = useState(true);

  const selectedAdherent = useMemo(() => {
    if (!selectedAdherentId) return null;

    return (
      managedAdherents.find(
        (adherent) => String(adherent.id) === String(selectedAdherentId),
      ) || null
    );
  }, [managedAdherents, selectedAdherentId]);

  const sectionContent = useMemo(() => {
    if (isConnected) {
      return {
        eyebrow: "Passages de grades",
        title: "Ma progression de ceinture.",
        subtitle:
          "Ce composant affiche le premier judoka géré. Vous pouvez basculer sur un autre profil.",
        note: null,
        primaryLabel: "Voir tous mes grades",
        primaryPath: "/passage-grade",
      };
    }

    return {
      eyebrow: "Progression technique",
      title: "Le suivi des grades devient lisible.",
      subtitle:
        "Judo Coach Pro valorise la dernière ceinture obtenue, l’historique des passages et la prochaine étape.",
      note: "Démonstration : l’historique ci-dessous provient du compte de démo utilisateur 66.",
      primaryLabel: "Créer mon compte gratuit",
      primaryPath: "/register",
    };
  }, [isConnected]);

  const handleNavigation = () => {
    navigate(sectionContent.primaryPath);
  };

  useEffect(() => {
    let isMounted = true;

    const fetchManagedAdherents = async () => {
      if (!isConnected) {
        if (isMounted) {
          setManagedAdherents([]);
          setSelectedAdherentId("");
          setAdherentsLoaded(true);
        }

        return;
      }

      setAdherentsLoaded(false);

      try {
        const response = await getAdherentsByUser();
        const adherents = normalizeAdherentsResponse(response);

        if (!isMounted) return;

        setManagedAdherents(adherents);

        setSelectedAdherentId((currentId) => {
          const currentStillExists = adherents.some(
            (adherent) => String(adherent.id) === String(currentId),
          );

          if (currentId && currentStillExists) {
            return currentId;
          }

          return adherents[0]?.id ? String(adherents[0].id) : "";
        });
      } catch (error) {
        console.error("Erreur lors de la récupération des adhérents :", error);

        if (isMounted) {
          setManagedAdherents([]);
          setSelectedAdherentId("");
        }
      } finally {
        if (isMounted) {
          setAdherentsLoaded(true);
        }
      }
    };

    fetchManagedAdherents();

    return () => {
      isMounted = false;
    };
  }, [isConnected]);

  useEffect(() => {
    let isMounted = true;

    const fetchSelectedAdherentGrades = async () => {
      if (!selectedAdherentId) {
        return {
          history: [],
          gradeCNData: null,
        };
      }

      const [historyResponse, gradeCNResponse] = await Promise.all([
        getBeltHistoryByAdherent(selectedAdherentId),
        checkGrade(selectedAdherentId).catch(() => null),
      ]);

      const history = normalizeBeltHistoryResponse(historyResponse).map(
        (grade) => ({
          ...grade,
          _homeAdherent: selectedAdherent
            ? {
                id: selectedAdherent.id,
                nom: selectedAdherent.nom,
                prenom: selectedAdherent.prenom,
                photo_url: selectedAdherent.photo_url,
              }
            : null,
        }),
      );

      return {
        history: dedupeGrades(history),
        gradeCNData: gradeCNResponse
          ? {
              ...gradeCNResponse,
              _homeAdherent: selectedAdherent
                ? {
                    id: selectedAdherent.id,
                    nom: selectedAdherent.nom,
                    prenom: selectedAdherent.prenom,
                  }
                : null,
            }
          : null,
      };
    };

    const fetchVisitorGrades = async () => {
      const response = await getHomeDemoBeltHistory();

      return {
        history: normalizeBeltHistoryResponse(response),
        gradeCNData: null,
      };
    };

    const fetchData = async () => {
      if (isConnected && !adherentsLoaded) {
        setLoading(true);
        return;
      }

      if (isConnected && !selectedAdherentId) {
        setGrades([]);
        setGradeCN(null);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const [beltListResponse, loadedData] = await Promise.all([
          getBeltsList(),
          isConnected ? fetchSelectedAdherentGrades() : fetchVisitorGrades(),
        ]);

        if (isMounted) {
          setBelts(normalizeBeltsResponse(beltListResponse));
          setGrades(sortGradesByDateDesc(loadedData.history));
          setGradeCN(loadedData.gradeCNData);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des grades :", error);

        if (isMounted) {
          setGrades([]);
          setBelts([]);
          setGradeCN(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [isConnected, adherentsLoaded, selectedAdherentId, selectedAdherent]);

  const displayedGrades = grades.slice(0, MAX_GRADES_HOME);
  const latestGrade = displayedGrades[0] || null;
  const previousGrades = displayedGrades.slice(1);
  const nextBelt = findNextBelt(latestGrade, belts);
  const hasGradeData = Boolean(latestGrade) || Boolean(gradeCN);
  const hasAdherents = managedAdherents.length > 0;

  return (
    <section className="relative overflow-hidden bg-white px-4 py-12 text-slate-950 dark:bg-[#07111f] dark:text-white sm:px-6 sm:py-14 lg:px-8">
      <div className="absolute -left-24 top-16 h-64 w-64 rounded-full bg-sky-500/10 blur-3xl" />
      <div className="absolute right-0 bottom-0 h-72 w-72 rounded-full bg-orange-500/10 blur-3xl" />

      <div className="relative mx-auto max-w-7xl">
        <div className="mb-6 grid gap-5 lg:grid-cols-[0.68fr_0.32fr] lg:items-end">
          <Slide direction="left" triggerOnce>
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-2 text-xs font-bold uppercase tracking-[0.18em] text-sky-700 shadow-sm dark:border-sky-400/20 dark:bg-sky-500/10 dark:text-sky-200">
                <FaLayerGroup />
                {sectionContent.eyebrow}
              </div>

              <h2 className="mt-4 font-heading text-3xl font-bold leading-tight text-slate-950 dark:text-white sm:text-4xl lg:text-5xl">
                {sectionContent.title}
              </h2>

              <p className="mt-3 max-w-2xl text-base font-medium leading-7 text-slate-600 dark:text-white/55">
                {sectionContent.subtitle}
              </p>
            </div>
          </Slide>

          <div className="grid gap-3">
            <AdherentSelect
              isConnected={isConnected}
              adherents={managedAdherents}
              selectedAdherentId={selectedAdherentId}
              onChange={setSelectedAdherentId}
            />

            <Fade direction="up" triggerOnce delay={120}>
              <div className="flex lg:justify-end">
                <button
                  type="button"
                  onClick={handleNavigation}
                  className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-orange-500 to-yellow-400 px-5 py-3 text-sm font-bold text-slate-950 shadow-lg shadow-orange-500/20 transition hover:-translate-y-0.5 active:translate-y-0 sm:w-auto lg:w-full"
                >
                  {sectionContent.primaryLabel}
                  <FaChevronRight className="text-xs" />
                </button>
              </div>
            </Fade>
          </div>
        </div>

        {sectionContent.note && (
          <Fade direction="up" triggerOnce>
            <div className="mb-5 rounded-[1.4rem] border border-orange-200 bg-orange-50 p-3 text-sm font-semibold leading-6 text-orange-800 dark:border-orange-400/20 dark:bg-orange-500/10 dark:text-orange-100">
              {sectionContent.note}
            </div>
          </Fade>
        )}

        {loading && <PassageGradeSkeleton />}

        {!loading && !hasGradeData && (
          <PassageGradeEmptyState
            isConnected={isConnected}
            hasAdherents={hasAdherents}
            onAction={handleNavigation}
          />
        )}

        {!loading && hasGradeData && (
          <>
            <div className="grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
              {latestGrade && (
                <LatestBeltCard grade={latestGrade} nextBelt={nextBelt} />
              )}

              <div className="space-y-3">
                <Fade direction="up" triggerOnce>
                  <div className="rounded-[1.5rem] border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-white/[0.05]">
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                      Historique récent
                    </p>

                    <h3 className="mt-1 font-heading text-xl font-bold text-slate-950 dark:text-white">
                      Derniers passages
                    </h3>

                    <p className="mt-1 text-sm font-medium leading-6 text-slate-500 dark:text-white/45">
                      {selectedAdherent
                        ? `Affichage des ceintures de ${getAdherentOptionLabel(
                            selectedAdherent,
                          )}.`
                        : "Les passages de ceinture restent visibles saison après saison."}
                    </p>
                  </div>
                </Fade>

                {previousGrades.length > 0 ? (
                  previousGrades.map((grade, index) => (
                    <GradeTimelineItem
                      key={grade?.id || `${grade?.belt_id}-${index}`}
                      grade={grade}
                      index={index}
                    />
                  ))
                ) : (
                  <Fade direction="up" triggerOnce>
                    <div className="rounded-[1.5rem] border border-slate-200 bg-white p-4 text-sm font-semibold text-slate-500 dark:border-white/10 dark:bg-white/[0.05] dark:text-white/50">
                      Aucun autre passage enregistré pour ce judoka.
                    </div>
                  </Fade>
                )}
              </div>
            </div>

            <GradeCnCard gradeCN={gradeCN} nextBelt={nextBelt} />
          </>
        )}
      </div>
    </section>
  );
}

export default PassageGradeHome;